import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Trash, Copy, Users, Ticket, Warning, ShieldCheck, Eye } from '@phosphor-icons/react';
import { LoginEventsModal } from './admin/LoginEventsModal';
import { SiteMusicAdmin } from './admin/SiteMusicAdmin';
import { ReviewsAdmin } from './admin/ReviewsAdmin';
import { AdminStatsPanel } from './admin/AdminStatsPanel';
import { Button } from '../components/ui/button';
import { BackToTop } from '../components/BackToTop';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Admin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showRedemptions, setShowRedemptions] = useState(null);
  const [redemptions, setRedemptions] = useState([]);

  const [codeName, setCodeName] = useState('');
  const [discountPercent, setDiscountPercent] = useState(100);
  const [maxRedemptions, setMaxRedemptions] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [description, setDescription] = useState('');
  const [codeType, setCodeType] = useState('subscription_trial'); // "document" | "subscription_trial"
  const [trialDays, setTrialDays] = useState(30);
  const [applicablePlans, setApplicablePlans] = useState([]); // empty = all plans
  const [creating, setCreating] = useState(false);

  const [flagged, setFlagged] = useState([]);
  const [flaggedLoading, setFlaggedLoading] = useState(true);
  const [eventsUser, setEventsUser] = useState(null);

  const fetchFlagged = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/flagged-users`, { withCredentials: true });
      setFlagged(r.data.flagged || []);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Flagged users fetch failed', error);
    } finally {
      setFlaggedLoading(false);
    }
  }, []);

  const clearFlag = async (userId) => {
    try {
      await axios.post(`${API}/admin/clear-flag/${userId}`, {}, { withCredentials: true });
      toast.success('Flag cleared');
      setFlagged((prev) => prev.filter((u) => u.user_id !== userId));
    } catch {
      toast.error('Could not clear flag');
    }
  };

  const fetchCodes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/promo-codes`, { withCredentials: true });
      setCodes(response.data.codes || []);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch codes:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!user?.is_admin) {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    fetchCodes();
    fetchFlagged();
  }, [user, navigate, fetchCodes, fetchFlagged]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        code: codeName.trim() || null,
        discount_percent: parseInt(discountPercent, 10),
        max_redemptions: maxRedemptions ? parseInt(maxRedemptions, 10) : null,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
        description: description.trim() || null,
        code_type: codeType,
        trial_days: codeType === 'subscription_trial' ? parseInt(trialDays, 10) : null,
        applicable_plans: codeType === 'subscription_trial' ? applicablePlans : [],
      };
      await axios.post(`${API}/admin/promo-codes`, payload, { withCredentials: true });
      toast.success('Promo code created');
      setShowCreate(false);
      setCodeName('');
      setDiscountPercent(100);
      setMaxRedemptions('');
      setExpiresAt('');
      setDescription('');
      setCodeType('subscription_trial');
      setTrialDays(30);
      setApplicablePlans([]);
      fetchCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create code');
    } finally {
      setCreating(false);
    }
  };

  const togglePlan = (key) => {
    setApplicablePlans((prev) => (prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]));
  };

  const PLAN_OPTIONS = [
    { key: 'sub_starter_monthly', label: 'Starter (£8/mo)' },
    { key: 'sub_pro_monthly', label: 'Pro (£15/mo)' },
    { key: 'sub_unlimited_monthly', label: 'Unlimited (£30/mo)' },
    { key: 'sub_creative_partner_yearly', label: 'Creative Partner (£79/yr)' },
  ];

  const handleDelete = async (code) => {
    if (!window.confirm(`Deactivate code "${code}"?`)) return;
    try {
      await axios.delete(`${API}/admin/promo-codes/${code}`, { withCredentials: true });
      toast.success('Code deactivated');
      fetchCodes();
    } catch (error) {
      toast.error('Failed to deactivate');
    }
  };

  const viewRedemptions = async (code) => {
    try {
      const response = await axios.get(`${API}/admin/promo-codes/${code}/redemptions`, { withCredentials: true });
      setRedemptions(response.data.redemptions || []);
      setShowRedemptions(code);
    } catch (error) {
      toast.error('Failed to load redemptions');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied "${code}"`);
  };

  const inputCls = "w-full px-4 py-3 rounded-lg border border-border bg-background text-ivory placeholder:text-ash focus:border-gold focus:ring-1 focus:ring-gold/40 outline-none transition-colors font-sans";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="inline-block w-12 h-12 border-2 border-border border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-background" data-testid="admin-page">
      <header className="h-16 border-b border-border flex items-center justify-between px-6 md:px-10">
        <div className="flex items-center gap-4">
          <Button
            onClick={() => navigate('/')}
            className="rounded-lg font-medium text-xs uppercase tracking-[0.2em] px-4 py-2 bg-transparent text-ivory border border-border hover:border-gold hover:text-gold"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft size={14} weight="duotone" className="mr-2" />
            Dashboard
          </Button>
          <h1 className="text-xl font-serif font-medium tracking-tight text-ivory">Admin</h1>
        </div>
        <p className="text-[11px] font-medium uppercase tracking-[0.28em] text-ash">{user?.email}</p>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-12 py-12 animate-fade-up">
        <AdminStatsPanel />

        <SiteMusicAdmin />

        <ReviewsAdmin />

        {/* Flagged accounts (account sharing detection) */}
        <section className="mb-12" data-testid="flagged-accounts-section">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-serif font-normal tracking-tight text-ivory flex items-center gap-3">
                <Warning size={22} weight="duotone" className="text-rose-400" /> Flagged Accounts
              </h2>
              <p className="text-sm text-bone font-sans mt-2">
                Accounts with suspicious sign-in patterns (e.g. 3+ different IPs in 24 hours).
              </p>
            </div>
            <span className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash">
              {flagged.length} flagged
            </span>
          </div>

          {flaggedLoading ? (
            <div className="panel-luxe p-6 text-center text-ash text-sm">Loading…</div>
          ) : flagged.length === 0 ? (
            <div className="panel-luxe p-8 text-center" data-testid="no-flagged-empty">
              <ShieldCheck size={28} weight="duotone" className="text-gold mx-auto mb-3" />
              <p className="text-bone font-sans text-sm">No flagged accounts. Account sharing detection is active.</p>
            </div>
          ) : (
            <div className="panel-luxe overflow-hidden">
              <table className="w-full">
                <thead className="bg-stone border-b border-border">
                  <tr>
                    {['User', 'Reason', 'Flagged At', 'Recent IPs', 'Actions'].map((h, i) => (
                      <th key={h} className={`text-[10px] font-medium uppercase tracking-[0.28em] text-ash p-4 ${i === 4 ? 'text-right' : 'text-left'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {flagged.map((u) => {
                    const ips = Array.from(new Set((u.recent_logins || []).map((e) => e.ip))).slice(0, 4);
                    return (
                      <tr key={u.user_id} className="border-t border-border/60 hover:bg-stone/40 transition-colors" data-testid={`flagged-row-${u.user_id}`}>
                        <td className="p-4">
                          <div className="font-medium text-ivory">{u.name || '—'}</div>
                          <div className="text-xs text-ash font-mono">{u.email}</div>
                        </td>
                        <td className="p-4 text-sm text-rose-400">{u.suspicious_reason || 'Suspicious activity'}</td>
                        <td className="p-4 text-xs text-bone">
                          {u.suspicious_flagged_at ? new Date(u.suspicious_flagged_at).toLocaleString() : '—'}
                        </td>
                        <td className="p-4 text-xs font-mono text-bone leading-relaxed">
                          {ips.length === 0 ? '—' : ips.map((ip) => (<div key={ip}>{ip}</div>))}
                        </td>
                        <td className="p-4 text-right">
                          <button
                            onClick={() => setEventsUser(u)}
                            className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash hover:text-gold px-3 py-2 rounded-md hover:bg-stone transition-colors inline-flex items-center gap-1.5 mr-1"
                            data-testid={`view-events-${u.user_id}`}
                            title="View full login history for this user"
                          >
                            <Eye size={12} weight="duotone" />
                            History
                          </button>
                          <button
                            onClick={() => clearFlag(u.user_id)}
                            className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash hover:text-gold px-3 py-2 rounded-md hover:bg-stone transition-colors"
                            data-testid={`clear-flag-${u.user_id}`}
                            title="Mark this account as reviewed and clear the flag"
                          >
                            Clear flag
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <div className="flex items-center justify-between mb-10">
          <div>
            <h2 className="text-3xl font-serif font-normal tracking-tight text-ivory flex items-center gap-3">
              <Ticket size={24} weight="duotone" className="text-gold" /> Promo Codes
            </h2>
            <p className="text-sm text-bone font-sans mt-2">{codes.length} total {codes.length === 1 ? 'code' : 'codes'}</p>
          </div>
          <Button
            onClick={() => setShowCreate(true)}
            className="rounded-lg font-medium text-sm tracking-wide px-6 py-3 bg-gold text-charcoal hover:bg-gold-dark"
            data-testid="create-code-btn"
          >
            <Plus size={16} weight="duotone" className="mr-2" />
            Create Code
          </Button>
        </div>

        {codes.length === 0 ? (
          <div className="panel-luxe p-16 text-center">
            <p className="text-bone font-sans">No promo codes yet. Create your first code to get started.</p>
          </div>
        ) : (
          <div className="panel-luxe overflow-hidden">
            <table className="w-full">
              <thead className="bg-stone border-b border-border">
                <tr>
                  {['Code','Type','Discount','Redemptions','Expires','Status','Actions'].map((h, i) => (
                    <th key={h} className={`text-[10px] font-medium uppercase tracking-[0.28em] text-ash p-4 ${i === 6 ? 'text-right' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {codes.map((c) => (
                  <tr key={c.code} className="border-t border-border/60 hover:bg-stone/40 transition-colors" data-testid={`code-row-${c.code}`}>
                    <td className="p-4 font-mono font-medium text-ivory">{c.code}</td>
                    <td className="p-4 text-xs font-sans">
                      {c.code_type === 'subscription_trial' ? (
                        <span className="text-emerald-300">
                          Trial · {c.trial_days || 0}d
                          {c.applicable_plans && c.applicable_plans.length > 0 ? ` · ${c.applicable_plans.length} plan${c.applicable_plans.length > 1 ? 's' : ''}` : ''}
                        </span>
                      ) : (
                        <span className="text-ash">Document</span>
                      )}
                    </td>
                    <td className="p-4 font-sans text-ivory">{c.discount_percent}%</td>
                    <td className="p-4 font-sans text-bone">
                      {c.redemption_count}
                      {c.max_redemptions ? ` / ${c.max_redemptions}` : ' / ∞'}
                    </td>
                    <td className="p-4 font-sans text-sm text-bone">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : 'Never'}</td>
                    <td className="p-4">
                      <span className={`text-[10px] font-medium uppercase tracking-[0.28em] ${c.is_active ? 'text-gold' : 'text-ash'}`}>
                        {c.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => copyCode(c.code)} className="p-2 rounded-md hover:bg-stone text-bone hover:text-gold transition-colors" title="Copy code" data-testid={`copy-${c.code}`}>
                          <Copy size={16} weight="duotone" />
                        </button>
                        <button onClick={() => viewRedemptions(c.code)} className="p-2 rounded-md hover:bg-stone text-bone hover:text-gold transition-colors" title="View redemptions" data-testid={`view-${c.code}`}>
                          <Users size={16} weight="duotone" />
                        </button>
                        {c.is_active && (
                          <button onClick={() => handleDelete(c.code)} className="p-2 rounded-md hover:bg-rose-950/40 text-bone hover:text-rose-400 transition-colors" title="Deactivate" data-testid={`delete-${c.code}`}>
                            <Trash size={16} weight="duotone" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="rounded-2xl border border-border bg-card max-w-md max-h-[90vh] overflow-y-auto" data-testid="create-code-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-serif font-normal tracking-tight text-ivory">Create Promo Code</DialogTitle>
            <DialogDescription className="text-sm text-bone font-sans">
              Leave the code field blank to auto-generate
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-2">
            {/* Code type selector */}
            <div>
              <label className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash block mb-2">Code type *</label>
              <div className="grid grid-cols-2 gap-2" data-testid="code-type-toggle">
                <button
                  type="button"
                  onClick={() => setCodeType('subscription_trial')}
                  className={`text-left border rounded-lg p-3 transition-colors ${codeType === 'subscription_trial' ? 'border-gold bg-gold/10' : 'border-border bg-stone/30 hover:border-gold/40'}`}
                  data-testid="code-type-subscription_trial"
                >
                  <div className="text-sm font-serif text-ivory">Subscription trial</div>
                  <div className="text-[10px] text-ash">Free (or discounted) period on a recurring plan</div>
                </button>
                <button
                  type="button"
                  onClick={() => setCodeType('document')}
                  className={`text-left border rounded-lg p-3 transition-colors ${codeType === 'document' ? 'border-gold bg-gold/10' : 'border-border bg-stone/30 hover:border-gold/40'}`}
                  data-testid="code-type-document"
                >
                  <div className="text-sm font-serif text-ivory">Document discount</div>
                  <div className="text-[10px] text-ash">% off a single per-document proofread</div>
                </button>
              </div>
            </div>

            <div>
              <label className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash block mb-2">Code (optional)</label>
              <input type="text" value={codeName} onChange={(e) => setCodeName(e.target.value.toUpperCase())} placeholder="LAUNCH50 (or leave blank)" className={`${inputCls} font-mono`} data-testid="code-name-input" />
            </div>

            <div>
              <label className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash block mb-2">
                Discount percent * {codeType === 'subscription_trial' && '(100 = fully free trial)'}
              </label>
              <input type="number" value={discountPercent} onChange={(e) => setDiscountPercent(e.target.value)} required min="1" max="100" className={inputCls} data-testid="discount-input" />
            </div>

            {codeType === 'subscription_trial' && (
              <>
                <div>
                  <label className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash block mb-2">Free period (days) *</label>
                  <input
                    type="number"
                    value={trialDays}
                    onChange={(e) => setTrialDays(e.target.value)}
                    required
                    min="1"
                    max="365"
                    placeholder="30"
                    className={inputCls}
                    data-testid="trial-days-input"
                  />
                  <p className="text-[10px] text-ash mt-1">Card is captured now — auto-renews at plan price when the free period ends.</p>
                </div>
                <div>
                  <label className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash block mb-2">Applicable plans (leave all unticked = all plans)</label>
                  <div className="grid grid-cols-1 gap-2" data-testid="applicable-plans">
                    {PLAN_OPTIONS.map((p) => (
                      <label key={p.key} className="flex items-center gap-2 text-sm text-bone font-sans cursor-pointer">
                        <input
                          type="checkbox"
                          checked={applicablePlans.includes(p.key)}
                          onChange={() => togglePlan(p.key)}
                          className="accent-gold"
                          data-testid={`plan-${p.key}`}
                        />
                        {p.label}
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}

            <div>
              <label className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash block mb-2">Max redemptions (optional)</label>
              <input type="number" value={maxRedemptions} onChange={(e) => setMaxRedemptions(e.target.value)} min="1" placeholder="Leave blank for unlimited" className={inputCls} data-testid="max-redemptions-input" />
            </div>
            <div>
              <label className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash block mb-2">Expires at (optional)</label>
              <input type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className={inputCls} data-testid="expires-input" />
            </div>
            <div>
              <label className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash block mb-2">Description (optional)</label>
              <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g., Launch promotion for early users" className={inputCls} data-testid="description-input" />
            </div>
            <Button
              type="submit"
              disabled={creating}
              className="w-full rounded-lg font-medium text-sm tracking-wide px-6 py-3 bg-gold text-charcoal hover:bg-gold-dark mt-2"
              data-testid="submit-code-btn"
            >
              {creating ? 'Creating…' : 'Create Code'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!showRedemptions} onOpenChange={() => setShowRedemptions(null)}>
        <DialogContent className="rounded-2xl border border-border bg-card max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="redemptions-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-serif font-normal tracking-tight text-ivory">Redemptions: <span className="text-gold">{showRedemptions}</span></DialogTitle>
            <DialogDescription className="text-sm text-bone font-sans">
              {redemptions.length} {redemptions.length === 1 ? 'user has' : 'users have'} redeemed this code
            </DialogDescription>
          </DialogHeader>
          {redemptions.length === 0 ? (
            <p className="text-sm text-bone font-sans">No redemptions yet.</p>
          ) : (
            <div className="max-h-96 overflow-y-auto space-y-2 mt-2">
              {redemptions.map((r) => (
                <div key={r.payment_session_id} className="p-4 rounded-lg border border-border bg-background">
                  <p className="font-medium font-sans text-ivory">{r.user_name}</p>
                  <p className="text-xs text-ash font-sans">{r.user_email}</p>
                  <div className="flex justify-between items-center mt-2 text-xs font-sans">
                    <span className="text-ash">{new Date(r.redeemed_at).toLocaleString()}</span>
                    <span className="font-medium text-gold">−£{(r.discount_amount || 0).toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <LoginEventsModal user={eventsUser} onClose={() => setEventsUser(null)} />
      <BackToTop />
    </div>
  );
};
